/**
 * Phase 41 Plan 02 — admin cron-jobs API client.
 *
 * Backend contract: Plan 41-01 cronJobService.ts CronJobRow + LastRunSummary.
 * Phase 56 Plan 56-01 extends CronJobRow with defaultPattern + disabled and
 * adds PUT/DELETE override endpoints.
 *
 * Endpoints:
 *   GET    /api/admin/cron-jobs                  → { data: CronJobRow[] }
 *   POST   /api/admin/cron-jobs/:id/run          → { data: { enqueuedJobId: string | null } }
 *   PUT    /api/admin/cron-jobs/:name            → { data: CronJobOverrideResult }
 *   DELETE /api/admin/cron-jobs/:name            → { data: CronJobOverrideResult }
 *
 * The DTO shapes here mirror the backend service exactly. If the backend
 * evolves, this file must be updated in the same commit.
 */
import { apiGet, apiPost, apiPut, apiDelete } from './client';

export interface LastRunSummary {
  /** ISO 8601 UTC timestamp from BullMQ; formatted to local-tz at render time. */
  finishedAt: string;
  durationMs: number | null;
  ok: boolean;
  /** First 80 chars of failedReason; null on successful runs (D-04). */
  errorSnippet: string | null;
}

export interface CronJobRow {
  schedulerId: string;
  /** null → D-02a "Unknown scheduler" (BullMQ has it but SCHEDULER_DESCRIPTIONS doesn't). */
  description: string | null;
  /** QUEUE_NAMES VALUE — e.g. 'maintenance-jobs'. Used directly in Bull Board deep-link (D-11). */
  queueName: string;
  pattern: string | null;
  /** null → D-02b "Not registered" (description map has it but BullMQ doesn't — staging case, D-14). */
  nextRunAt: string | null;
  lastScheduled: LastRunSummary | null;
  lastManual: LastRunSummary | null;
  isRegistered: boolean;
  hasDescription: boolean;
  /** Phase 56: registry default pattern; null when scheduler has no registry entry. */
  defaultPattern: string | null;
  /** Phase 56: true when a cron_job_overrides row exists with disabled=true. */
  disabled: boolean;
}

export interface RunCronJobResult {
  enqueuedJobId: string | null;
}

/** Phase 56 (CRON-CFG-03/04) — input for PUT /api/admin/cron-jobs/:name */
export interface UpdateCronJobOverrideInput {
  cronPattern?: string;
  disabled?: boolean;
}

/** Phase 56 (CRON-CFG-03/04) — response from PUT + DELETE /api/admin/cron-jobs/:name */
export interface CronJobOverrideResult {
  name: string;
  pattern: string | null;
  defaultPattern: string;
  /** F7-004: null when `disabled=true` (nothing scheduled); otherwise the live pattern. */
  effectivePattern: string | null;
  disabled: boolean;
}

export async function getCronJobs(): Promise<CronJobRow[]> {
  const res = await apiGet<{ data: CronJobRow[] }>('/admin/cron-jobs');
  return res.data;
}

export async function runCronJob(schedulerId: string): Promise<RunCronJobResult> {
  // encodeURIComponent is defensive: SCHEDULER_DESCRIPTIONS keys match
  // /^[a-z0-9-]+$/ so encoding is a no-op for valid input, but it mirrors
  // the server-side T-02 pattern.
  const res = await apiPost<{ data: RunCronJobResult }>(
    `/admin/cron-jobs/${encodeURIComponent(schedulerId)}/run`
  );
  return res.data;
}

/** Phase 56 (CRON-CFG-03) — PUT /api/admin/cron-jobs/:name */
export async function updateCronJobOverride(
  name: string,
  input: UpdateCronJobOverrideInput
): Promise<CronJobOverrideResult> {
  const res = await apiPut<{ data: CronJobOverrideResult }>(
    `/admin/cron-jobs/${encodeURIComponent(name)}`,
    input
  );
  return res.data;
}

/** Phase 56 (CRON-CFG-04) — DELETE /api/admin/cron-jobs/:name */
export async function resetCronJobOverride(name: string): Promise<CronJobOverrideResult> {
  const res = await apiDelete<{ data: CronJobOverrideResult }>(
    `/admin/cron-jobs/${encodeURIComponent(name)}`
  );
  return res.data;
}
